import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSearch, toUrlParams } from "../contexts/SearchContext";
import { useOrg } from "../contexts/OrganizationContext";
import { useCart } from "../contexts/CartContext";
import { useMap } from "../contexts/MapContext";
import { useBodyClass } from "../hooks/useBodyClass";
import { useContentTop } from "../hooks/useContentTop";
import { useMapSearch } from "../hooks/useMapSearch";
import MapContent from "../components/Map/MapContent";
import SearchResultsSidebar from "../components/Search/SearchResultsSidebar";
import SearchResultsWorkspace from "../components/Search/SearchResultsWorkspace";
import ClaimAgencyModal from "../components/Search/Detail/ClaimAgencyModal";
import { parseLatLng } from "../utils/geo";

const MAP_VIEW = "map";
const LIST_VIEW = "list";
const VIEW_PARAM = "view";

// A relative URL (e.g. "/search?terms=food") can't be parsed by `new URL()` on its own,
// so these helpers round-trip through an arbitrary absolute base and strip it back off.
function parseRelativeUrl(relativeUrl) {
  return new URL(relativeUrl, window.location.origin);
}

function formatRelativeUrl(url) {
  return url.href.slice(url.origin.length);
}

function setViewParam(url, view) {
  if (view === MAP_VIEW) {
    url.searchParams.set(VIEW_PARAM, MAP_VIEW);
  } else {
    url.searchParams.delete(VIEW_PARAM);
  }
  return url;
}

function getViewParam(searchParams) {
  return searchParams.get(VIEW_PARAM) === MAP_VIEW ? MAP_VIEW : LIST_VIEW;
}

function omitViewParam(searchParams) {
  const nextParams = new URLSearchParams(searchParams);
  nextParams.delete(VIEW_PARAM);
  return nextParams.toString();
}

function relativeUrlWithView(relativeUrl, view) {
  return formatRelativeUrl(setViewParam(parseRelativeUrl(relativeUrl), view));
}

function searchUrlForView(queryString, view) {
  const url = parseRelativeUrl("/search");
  url.search = queryString;
  return formatRelativeUrl(setViewParam(url, view));
}

function queryStringWithoutView(relativeUrl) {
  return omitViewParam(parseRelativeUrl(relativeUrl).searchParams);
}

function SearchPage() {
  const [urlSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { organization } = useOrg();
  const { itemCount } = useCart();
  const mapContext = useMap();
  const { requestMap, setCurrentPage } = mapContext;
  const {
    params, results, relatedTerms, totalResults, lastPage, loading,
    search, loadPage, setParams, resetFilters, fromUrl,
  } = useSearch();

  const { handleDragEnd } = useMapSearch({
    search,
    setParams,
    organization,
    params,
    mapContext,
  });

  useBodyClass("page-search");
  useContentTop();

  const [selectedResultId, setSelectedResultId] = React.useState(null);
  const [highlightedResultId, setHighlightedResultId] = React.useState(null);
  const [claimAgency, setClaimAgency] = React.useState(null);
  const [savedResourceIds, setSavedResourceIds] = React.useState(new Set());
  const [showScreenerConfirm, setShowScreenerConfirm] = React.useState(false);
  const [activeView, setActiveView] = React.useState(() => getViewParam(urlSearchParams));

  // Detect screener context from URL params
  const responseId = params.response_id || urlSearchParams.get("response_id");
  const surveyName = params.survey_name || urlSearchParams.get("survey_name");
  const screenerToken = params.token || urlSearchParams.get("token");

  function handleResourceSaved(programSiteId) {
    setSavedResourceIds((prev) => new Set(prev).add(programSiteId));
    if (sessionStorage.getItem("screenerConfirmation") !== "hide") {
      setShowScreenerConfirm(true);
    }
  }

  function handleDismissScreenerConfirm() {
    setShowScreenerConfirm(false);
  }

  function handleDoNotShowAgain() {
    setShowScreenerConfirm(false);
    sessionStorage.setItem("screenerConfirmation", "hide");
  }

  // Listen for claimAgency events from SearchResultCard
  React.useEffect(() => {
    function handleClaimAgency(e) {
      setClaimAgency(e.detail);
    }
    window.addEventListener("claimAgency", handleClaimAgency);
    return () => window.removeEventListener("claimAgency", handleClaimAgency);
  }, []);

  const mapCenter = params.coords || organization.serviceLocale?.geo?.mapCenter || { lat: 41.6, lng: -72.7 };

  // Prepare map results with latLng
  const mapResults = React.useMemo(
    () =>
      results.map((r) => ({
        ...r,
        latLng: r.latLng || parseLatLng(r.location),
        addressLine: [r.address_1, r.address_2, r.city, r.state].filter(Boolean).join(", "),
      })).filter((r) => r.latLng && r.latLng.lat && r.latLng.lng),
    [results]
  );

  // The Google map should only be constructed after the map view is opened.
  React.useEffect(() => {
    if (activeView !== MAP_VIEW) {
      setCurrentPage(null);
      return undefined;
    }

    requestMap();
    setCurrentPage("search");

    return () => {
      setCurrentPage(null);
    };
  }, [activeView, requestMap, setCurrentPage]);

  // Cache results and enable drag-to-search
  React.useEffect(() => {
    if (mapResults.length > 0) {
      mapContext.setSearchResults(mapResults);
    }
    mapContext.setDragEnabled(activeView === MAP_VIEW);
    mapContext.onDragEnd(handleDragEnd);
  }, [mapResults, mapContext, handleDragEnd, activeView]);

  React.useEffect(() => {
    if (activeView !== MAP_VIEW || !mapContext.mapInstance || typeof google === "undefined") return;

    window.requestAnimationFrame(() => {
      google.maps.event.trigger(mapContext.mapInstance, "resize");
    });
  }, [activeView, mapContext.mapInstance]);

  // Counter: incremented when URL-driven search starts, decremented when loading finishes
  const urlSearchPending = React.useRef(0);

  function hasSearchCriteria(parsed) {
    return (parsed.terms && parsed.terms.length > 0) ||
      (parsed.taxonomy_code && parsed.taxonomy_code.length > 0) ||
      (parsed.combined_taxonomy_ids && parsed.combined_taxonomy_ids.length > 0) ||
      parsed.response_id || parsed.survey_name;
  }

  // Run search from URL params on mount AND when URL changes (browser back/forward)
  const urlString = urlSearchParams.toString();
  const searchUrlString = omitViewParam(urlSearchParams);
  const prevUrlString = React.useRef(searchUrlString);

  function navigateWhenSearchChanged(newUrl, options) {
    const newQs = queryStringWithoutView(newUrl);

    if (newQs === searchUrlString) return;

    prevUrlString.current = newQs;
    navigate(newUrl, options);
  }

  React.useEffect(() => {
    const nextView = getViewParam(urlSearchParams);
    setActiveView((currentView) => (currentView === nextView ? currentView : nextView));
  }, [urlString]);

  React.useEffect(() => {
    if (prevUrlString.current === searchUrlString) return;
    prevUrlString.current = searchUrlString;
    const parsed = fromUrl(searchUrlString);
    if (!hasSearchCriteria(parsed)) return;
    urlSearchPending.current += 1;
    search(parsed, { reset: true });
  }, [searchUrlString]);

  // Initial search on mount
  const mounted = React.useRef(false);
  React.useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      const parsed = fromUrl(searchUrlString);
      if (!hasSearchCriteria(parsed)) return;
      urlSearchPending.current += 1;
      search(parsed, { reset: true });
    }
  }, []);

  // Sync search params to URL after searches complete (only for user-initiated searches)
  const wasLoading = React.useRef(false);
  React.useEffect(() => {
    if (loading) {
      wasLoading.current = true;
      return;
    }
    if (!mounted.current || !wasLoading.current) return;
    wasLoading.current = false;

    const urlDriven = urlSearchPending.current > 0;
    if (urlDriven) {
      urlSearchPending.current -= 1;
    }

    navigateWhenSearchChanged(
      relativeUrlWithView(toUrlParams(params), activeView),
      urlDriven ? { replace: true } : undefined
    );
  }, [params, loading, activeView]);

  function handleViewChange(view) {
    setActiveView(view);
    const nextUrl = searchUrlForView(urlString, view);
    const currentUrl = searchUrlForView(urlString, getViewParam(urlSearchParams));

    if (nextUrl !== currentUrl) {
      navigate(nextUrl, { replace: true });
    }

    sessionStorage.setItem("lastSearchUrl", nextUrl);
  }

  function handleSelectResult(result) {
    sessionStorage.setItem("lastSearchUrl", searchUrlForView(urlString, activeView));
    navigate(`/search/${result.program_site_id}`);
  }

  function handleIndicateResult(result) {
    setHighlightedResultId(result.program_site_id);
  }

  function handleRestoreResult() {
    setHighlightedResultId(null);
  }

  function handleMarkerClick(result) {
    setSelectedResultId(result ? result.program_site_id : null);
  }

  function handlePageChange(page) {
    loadPage(page);
    window.scrollTo(0, 0);
  }

  function handleRelatedSearch(term) {
    resetFilters();
    search({ terms: [term.name], taxonomy_code: [term.code], page: 1 });
  }

  // Add split-content-region class to #content-region (matches Marionette behavior)
  React.useEffect(() => {
    const contentRegion = document.getElementById("content-region");
    if (contentRegion) contentRegion.classList.add("split-content-region");
    return () => {
      if (contentRegion) contentRegion.classList.remove("split-content-region");
    };
  }, []);

  return (
    <div className={`search-layout search-results-page-layout${activeView === "map" ? " search-results-page-layout--map" : ""}`}>
      <MapContent
        results={mapResults}
        selectedResultId={selectedResultId}
        highlightedResultId={highlightedResultId}
        onMarkerClick={handleMarkerClick}
        fitToResults={activeView === "map"}
      />

      <SearchResultsSidebar
        itemCount={itemCount}
      />
      <SearchResultsWorkspace
        activeView={activeView}
        onViewChange={handleViewChange}
        responseId={responseId}
        screenerToken={screenerToken}
        surveyName={surveyName}
        category={params.category}
        relatedTerms={relatedTerms}
        onRelatedSearch={handleRelatedSearch}
        loading={loading}
        results={results}
        totalResults={totalResults}
        currentPage={params.page || 1}
        lastPage={lastPage}
        perPage={params.per_page || 25}
        searchCoords={params.coords}
        selectedResultId={selectedResultId}
        onSelectResult={handleSelectResult}
        onIndicateResult={handleIndicateResult}
        onRestoreResult={handleRestoreResult}
        onClearSelectedResult={() => setSelectedResultId(null)}
        savedResourceIds={savedResourceIds}
        hasSearchTerms={(params.terms && params.terms.length > 0) || (params.taxonomy_code && params.taxonomy_code.length > 0)}
        onResourceSaved={handleResourceSaved}
        pageNum={params.page || 1}
        onPageChange={handlePageChange}
      />
      <ClaimAgencyModal
        agencyName={claimAgency?.agencyName}
        agencyNumber={claimAgency?.agencyNumber}
        show={!!claimAgency}
        onClose={() => setClaimAgency(null)}
      />
      {showScreenerConfirm && (
        <div className="add-to-screener-confirmation modal" role="dialog" tabIndex="-1" style={{ display: "block", marginTop: 220, zIndex: 9999 }}>
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-body">
                <a className="close-modal" aria-label="Close" onClick={handleDismissScreenerConfirm} role="button" tabIndex={0}>
                  <span aria-hidden="true">&times;</span>
                </a>
                <div className="content-container">
                  <h5 className="direct confirmation-header">Automatically Saved To Screener</h5>
                  <h4 className="headliner confirmation-header">{surveyName || ""}</h4>
                  <p>
                    This resource can be viewed on your screener summary page. It has also been added to an automatically generated
                    resource list that can be found under your account.
                  </p>
                  <div className="button-container">
                    <button className="btn btn-default do-not-show-btn" onClick={handleDoNotShowAgain}>
                      Don&apos;t show this message again
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showScreenerConfirm && <div className="modal-backdrop fade in" />}
    </div>
  );
}

export default SearchPage;
